import { useAuthContext } from "../../auth/state/AuthContext";
import { userAtom, hasUWHCMessageAtom, themeAtom } from "../../shared/atoms";
import { log, logV } from "../../shared/services/debug-logger";
import { datadogRum } from "@datadog/browser-rum";
import { useAtom, useAtomValue } from "jotai";
import { cloneElement, forwardRef, isValidElement, ReactElement, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

type ChatOverlayProps = {
  children: React.ReactNode;
  onClose?: () => void;
  buttonLabel?: string;
};

export const ChatOverlay = forwardRef<HTMLDivElement, ChatOverlayProps>(
  ({ children, onClose, buttonLabel = "Open chat" }, ref) => {
    const [open, setOpen] = useState(false);

    const { sub: email } = useAuthContext().state.user?.accessToken || {};
    const [hasChatMessage] = useAtom(hasUWHCMessageAtom);
    const theme = useAtomValue(themeAtom);
    const chatUi = theme.uwhcAssistant.chatUi;

    const user = useAtomValue(userAtom);
    const close = () => {
      log("ChatOverlay - close");
      setOpen(false);
      if (hasChatMessage) {
        sendChatSessiontoDataDog();
      }

      onClose?.();
    };

    const openChat = () => {
      log("ChatOverlay - openChat");
      setOpen(true);
    };
    logV("ChatOverlay - BUILD", { open });

    const sendChatSessiontoDataDog = () => {
      datadogRum.addAction("sendUWHCUserSession", {
        ext_user_email: email,
        submittedBy: user?.name ?? "",
      });
    };

    useEffect(() => {
      if (!open) return;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") close();
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
      <>
        {/* Launcher button */}
        <button
          type="button"
          aria-label={buttonLabel}
          aria-expanded={open}
          onClick={openChat}
          className={twMerge(
            "fixed bottom-[160px] right-[40px] z-[1000] rounded-full p-4 shadow-lg transition focus:outline-none focus:ring-2 active:scale-95",
            chatUi.launcherButton,
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill={theme.uwhcAssistant.buttonCenterIcon}
            aria-hidden="true"
          >
            <path d="M12 3c5.06 0 9 3.44 9 7.67 0 4.22-3.94 7.66-9 7.66-.92 0-1.8-.11-2.62-.32-.23-.06-.48-.02-.68.11l-3.34 2.14a.75.75 0 0 1-1.14-.64l.08-3.03c.01-.24-.09-.47-.27-.63A7.36 7.36 0 0 1 3 10.67C3 6.44 6.94 3 12 3z" />
          </svg>
        </button>

        {/* Modal overlay */}
        {open && (
          <div
            className={twMerge("fixed inset-0 z-[1000] flex items-center justify-center", chatUi.overlayBackdrop)}
            role="dialog"
            aria-modal="true"
            onClick={close} // backdrop closes
          >
            <div
              ref={ref}
              className="h-[min(86vh,880px)] w-[min(1120px,calc(100vw-48px))] overflow-hidden"
              onClick={(e) => e.stopPropagation()} // prevent backdrop close
            >
              {isValidElement(children)
                ? cloneElement(children as ReactElement<{ onClose?: () => void }>, { onClose: close })
                : children}
            </div>
          </div>
        )}
      </>
    );
  },
);

ChatOverlay.displayName = "OverlayCmp";
